import React, { useId } from "react";
import ExportButton from "../../../ui/ExportButton";
import filterStyles from "../../../filters/FilterPanel.module.css";
import chartStyles from "../../analysis/Charts.module.css";
import layoutStyles from "../../../../styles/utilities/layout.module.css";
import styles from "./ChartControls.module.css";
import formStyles from "../../../../styles/components/forms.module.css";

type Mode = "line" | "column" | string;
type ExportFormat = "csv" | "pdf" | string;

export interface ChartControlsProps {
  showModeControls?: boolean;
  controlsGrid?: boolean;
  leftMode?: Mode;
  rightMode?: Mode;
  singleMode?: Mode;
  onLeftModeChange?: (mode: Mode) => void;
  onRightModeChange?: (mode: Mode) => void;
  onSingleModeChange?: (mode: Mode) => void;

  isDualChart?: boolean;

  showTotalControl?: boolean;
  showTotal?: boolean;
  onShowTotalChange?: (show: boolean) => void;

  showExportButton?: boolean;
  onExport?: (format: ExportFormat) => void;

  showExpandButton?: boolean;
  onExpand?: () => void;
  expandButtonColor?: string;
  expandTitle?: string;

  children?: React.ReactNode;
}

const ChartControls: React.FC<ChartControlsProps> = ({
  showModeControls = true,
  controlsGrid = false,
  leftMode,
  rightMode,
  singleMode,
  onLeftModeChange,
  onRightModeChange,
  onSingleModeChange,
  isDualChart = false,
  showTotalControl = false,
  showTotal,
  onShowTotalChange,
  showExportButton = true,
  onExport,
  children,
}) => {
  const id = useId();
  return (
    <div style={{ marginTop: "30px", marginBottom: "14px" }}>
      {controlsGrid && !isDualChart ? (
        <div
          className={`${layoutStyles.grid} ${layoutStyles.gridCols2} ${layoutStyles.itemsCenter} ${layoutStyles.gap4}`}
        >
          <div
            className={`${layoutStyles.flexRow} ${layoutStyles.itemsCenter} ${layoutStyles.gap2}`}
          >
            {showModeControls ? (
              (() => {
                const uid = id + "-single";
                return (
                  <div
                    className={`${layoutStyles.flexRow} ${layoutStyles.itemsCenter} ${layoutStyles.gap3}`}
                  >
                    <label className={`${filterStyles.itemLabel} ${layoutStyles.itemsCenter}`}>
                      <input
                        type="radio"
                        name={uid}
                        value="line"
                        checked={singleMode === "line"}
                        onChange={() => onSingleModeChange?.("line")}
                        className={filterStyles.radio}
                      />
                      <span className={filterStyles.labelText}>Line</span>
                    </label>
                    <label className={`${filterStyles.itemLabel} ${layoutStyles.itemsCenter}`}>
                      <input
                        type="radio"
                        name={uid}
                        value="column"
                        checked={singleMode === "column"}
                        onChange={() => onSingleModeChange?.("column")}
                        className={filterStyles.radio}
                      />
                      Stacked
                    </label>
                  </div>
                );
              })()
            ) : (
              <div />
            )}
          </div>

          <div
            className={`${layoutStyles.flexRow} ${layoutStyles.itemsCenter} ${layoutStyles.justifyBetween} ${layoutStyles.gap4} ${layoutStyles.wFull}`}
          >
            <div
              className={`${layoutStyles.flexRow} ${layoutStyles.itemsCenter} ${layoutStyles.gap4}`}
            >
              {children}
            </div>

            {showTotalControl && (
              <label
                className={`${layoutStyles.flexRow} ${layoutStyles.itemsCenter} ${layoutStyles.gap2} ${filterStyles.showTotalLabelWrapper}`}
                style={{
                  paddingLeft: "0.75rem",
                  paddingRight: "0.75rem",
                  height: "2.25rem",
                  whiteSpace: "nowrap",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!showTotal}
                  onChange={(e) => onShowTotalChange?.(e.target.checked)}
                  className={filterStyles.checkbox}
                />
                <span className={filterStyles.showTotalLabel}>Show total</span>
              </label>
            )}
          </div>
        </div>
      ) : (
        <div
          className={`${layoutStyles.flexRow} ${layoutStyles.flexWrap} ${layoutStyles.itemsCenter} ${layoutStyles.gap4}`}
        >
          {showModeControls && isDualChart && (
            <>
              <div className={styles.modeGroup}>
                <span className={styles.axisLabel}>Left (Volume):</span>
                {(() => {
                  const uidL = id + "-left";
                  return (
                    <div className={styles.modeOptions}>
                      <label className={formStyles.label}>
                        <input
                          type="radio"
                          name={uidL}
                          value="line"
                          checked={leftMode === "line"}
                          onChange={() => onLeftModeChange?.("line")}
                          className={formStyles.radio}
                        />
                        <span className={styles.modeLabel}>Line</span>
                      </label>
                      <label className={formStyles.label}>
                        <input
                          type="radio"
                          name={uidL}
                          value="column"
                          checked={leftMode === "column"}
                          onChange={() => onLeftModeChange?.("column")}
                          className={formStyles.radio}
                        />
                        <span className={styles.modeLabel}>Stacked</span>
                      </label>
                    </div>
                  );
                })()}
              </div>
              <div className={styles.modeGroup}>
                <span className={styles.axisLabel}>Right (Count):</span>
                {(() => {
                  const uidR = id + "-right";
                  return (
                    <div className={styles.modeOptions}>
                      <label className={formStyles.label}>
                        <input
                          type="radio"
                          name={uidR}
                          value="line"
                          checked={rightMode === "line"}
                          onChange={() => onRightModeChange?.("line")}
                          className={formStyles.radio}
                        />
                        <span className={styles.modeLabel}>Line</span>
                      </label>
                      <label className={formStyles.label}>
                        <input
                          type="radio"
                          name={uidR}
                          value="column"
                          checked={rightMode === "column"}
                          onChange={() => onRightModeChange?.("column")}
                          className={formStyles.radio}
                        />
                        <span className={styles.modeLabel}>Stacked</span>
                      </label>
                    </div>
                  );
                })()}
              </div>
            </>
          )}

          {showModeControls && !isDualChart && (
            <div className={styles.modeGroup}>
              {(() => {
                const uid2 = id + "-single-2";
                return (
                  <div className={styles.modeOptions}>
                    <label className={formStyles.label}>
                      <input
                        type="radio"
                        name={uid2}
                        value="line"
                        checked={singleMode === "line"}
                        onChange={() => onSingleModeChange?.("line")}
                        className={formStyles.radio}
                      />
                      <span className={styles.modeLabel}>Line</span>
                    </label>
                    <label className={formStyles.label}>
                      <input
                        type="radio"
                        name={uid2}
                        value="column"
                        checked={singleMode === "column"}
                        onChange={() => onSingleModeChange?.("column")}
                        className={formStyles.radio}
                      />
                      <span className={styles.modeLabel}>Stacked</span>
                    </label>
                  </div>
                );
              })()}
            </div>
          )}

          {showTotalControl && (
            <label className={styles.showTotalLabel}>
              <input
                type="checkbox"
                checked={!!showTotal}
                onChange={(e) => onShowTotalChange?.(e.target.checked)}
                className={formStyles.checkbox}
              />
              <span className={styles.showTotalText}>Show total</span>
            </label>
          )}

          {children}
        </div>
      )}

      <div className={`${layoutStyles.flexRow} ${layoutStyles.itemsCenter} ${layoutStyles.gap2}`}>
        {showExportButton && (
          <div
            className={`${layoutStyles.inlineFlex} ${layoutStyles.itemsCenter} ${layoutStyles.gap2}`}
          >
            <ExportButton
              label="CSV"
              onClick={() => onExport?.("csv")}
              className={`${chartStyles.controlButton} ${styles.exportButtonRounded}`}
            />

            <ExportButton
              label="PDF"
              onClick={() => onExport?.("pdf")}
              className={`${chartStyles.controlButton} ${styles.exportButtonRounded}`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartControls;
